import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Wifi, WifiOff, Gauge, Battery, ChevronRight } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color, isOnline) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: ${color};
        border: 3px solid ${isOnline ? '#00FF00' : '#888'};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      ">
        <div style="
          width: 12px;
          height: 12px;
          background: white;
          border-radius: 50%;
          transform: rotate(45deg);
        "></div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Map center adjustment component
const MapController = ({ devices }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (devices.length > 0) {
      const bounds = L.latLngBounds(
        devices
          .filter(d => d.gps_lat && d.gps_lon)
          .map(d => [d.gps_lat, d.gps_lon])
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }
  }, [devices, map]);
  
  return null;
};

const DeviceMap = ({ devices, height = '400px', showControls = true }) => {
  const navigate = useNavigate();
  
  // Calculate center from devices or use default (Nanshan Village, Taiwan)
  const center = useMemo(() => {
    const validDevices = devices.filter(d => d.gps_lat && d.gps_lon);
    if (validDevices.length === 0) {
      return [24.42, 121.38]; // Default: Nanshan Village
    }
    const avgLat = validDevices.reduce((sum, d) => sum + d.gps_lat, 0) / validDevices.length;
    const avgLon = validDevices.reduce((sum, d) => sum + d.gps_lon, 0) / validDevices.length;
    return [avgLat, avgLon];
  }, [devices]);

  // Get marker color based on valve state
  const getMarkerColor = (device) => {
    if (device.status !== 'online') return '#666666'; // Offline - gray
    if (device.valve_state === 'open') return '#00CC66'; // Open - green
    return '#0088FF'; // Closed - blue
  };

  return (
    <div className="relative rounded-lg overflow-hidden border border-border" style={{ height }}>
      <MapContainer
        center={center}
        zoom={14}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapController devices={devices} />
        
        {devices.map((device) => {
          if (!device.gps_lat || !device.gps_lon) return null;
          
          const isOnline = device.status === 'online';
          const icon = createCustomIcon(getMarkerColor(device), isOnline);
          
          return (
            <Marker
              key={device.id}
              position={[device.gps_lat, device.gps_lon]}
              icon={icon}
            >
              <Popup className="device-popup">
                <div className="min-w-[200px] p-2">
                  {/* Device Name */}
                  <h3 className="font-bold text-lg mb-2 text-gray-900">{device.name}</h3>
                  
                  {/* Status Row */}
                  <div className="flex items-center gap-2 mb-2">
                    {isOnline ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <Wifi className="w-4 h-4" />
                        Online / 在線
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-500 text-sm">
                        <WifiOff className="w-4 h-4" />
                        Offline / 離線
                      </span>
                    )}
                  </div>
                  
                  {/* Device Info */}
                  <div className="space-y-1 text-sm text-gray-700 mb-3">
                    <div className="flex justify-between">
                      <span>Valve / 閥門:</span>
                      <span className={device.valve_state === 'open' ? 'text-green-600 font-medium' : ''}>
                        {device.valve_state === 'open' ? 'OPEN / 開啟' : 'CLOSED / 關閉'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        Pressure:
                      </span>
                      <span>{device.pressure} bar</span>
                    </div>
                    {device.battery !== undefined && device.battery !== null && (
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-1">
                          <Battery className="w-3 h-3" />
                          Battery:
                        </span>
                        <span className={device.battery < 20 ? 'text-red-600' : ''}>
                          {device.battery}%
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Location */}
                  {device.location && (
                    <p className="text-xs text-gray-500 mb-3">
                      📍 {device.location}
                    </p>
                  )}
                  
                  {/* Control Button */}
                  {showControls && (
                    <Button
                      size="sm"
                      onClick={() => navigate(`/app/device/${device.id}`)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Control / 控制
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-black/80 rounded-lg p-3 z-[1000]">
        <p className="text-xs text-white font-bold mb-2">Legend / 圖例</p>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#00CC66] border-2 border-green-400"></div>
            <span className="text-white">Online + Open / 在線+開啟</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#0088FF] border-2 border-green-400"></div>
            <span className="text-white">Online + Closed / 在線+關閉</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#666666] border-2 border-gray-400"></div>
            <span className="text-white">Offline / 離線</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceMap;
